import React, { useState } from 'react'
import { Button, Card, Result, Space, Table } from 'antd'
import { useParams } from 'react-router'
import { DragDropContext } from 'react-beautiful-dnd'
import ComponentTableWrapper from './ComponentTableWrapper'
import ComponentTableRow from './ComponentTableRow'
import { openNotificationWithIcon } from '../../../../utils/notification.helper'
import { useAssignmentAccess } from '../../components/access/Access'
import { AssignmentPermissions } from '../../../../constants/assignment-permissions'
import { useFetchComponentsQuery, useReorderComponentsMutation } from '../../../../store/apis/component.api'

const ComponentsTable = () => {
    const { token, componentId } = useParams()

    let {
        data: components,
        isFetching: isFetchingComponents,
        error: errorFetchingComponents,
        refetch: refetchComponents,
    } = useFetchComponentsQuery({ token })
    components = components?.filter(component => component.name !== 'UnitTests999')
    const [reorderComponents, reorderComponentsResult] = useReorderComponentsMutation()
    const [newTableData, setNewTableData] = useState(null)

    const hasEditingPermission = useAssignmentAccess([AssignmentPermissions.CAN_EDIT])

    if (errorFetchingComponents)
        return (
            <Result
                status='error'
                title='Components fetch failed'
                subTitle={errorFetchingComponents}
                extra={[
                    <Button key='try' onClick={() => refetchComponents()} loading={isFetchingComponents}>
                        Try Again
                    </Button>,
                ]}
            />
        )

    const tableColumns = [
        ...(hasEditingPermission ? [{ key: 'sort', width: '1%' }] : []),
        {
            title: 'Name',
            dataIndex: 'name',
        },
    ]

    const tableData = components?.map(component => ({
        id: component.id,
        name: component.name,
    }))

    const onDragEnd = ({ destination, source, draggableId }) => {
        if (!destination) return
        if (destination.index === source.index) return

        const reorderedTableData = Array.from(newTableData || tableData)
        reorderedTableData.splice(source.index, 1) // removing the item
        reorderedTableData.splice(
            destination.index,
            0,
            (newTableData || tableData).find(data => data.id.toString() === draggableId),
        ) // pushing in the new position
        setNewTableData(reorderedTableData)
    }

    const tableComponents = hasEditingPermission
        ? {
              body: {
                  wrapper: ComponentTableWrapper,
                  row: ComponentTableRow,
              },
          }
        : null

    const handleSaveClick = async () => {
        const idToOrderMap = {}
        newTableData.forEach((data, index) => (idToOrderMap[data.id] = index))
        const reorderComponentsResponse = await reorderComponents({ token, componentId, idToOrderMap })
        if ('data' in reorderComponentsResponse) {
            setNewTableData(null)
            openNotificationWithIcon('success', 'Components order saved successfully')
        } else if (reorderComponentsResponse?.error) {
            openNotificationWithIcon('error', 'Components order save failed', reorderComponentsResponse.error)
        }
    }

    const actionButtons = (
        <Space size={8}>
            <Button danger onClick={() => setNewTableData(null)}>
                Revert
            </Button>
            <Button type='primary' onClick={handleSaveClick} loading={reorderComponentsResult.isLoading}>
                Save
            </Button>
        </Space>
    )

    return (
        <Card title='Components' extra={newTableData ? actionButtons : null}>
            <DragDropContext onDragEnd={onDragEnd}>
                <Table
                    columns={tableColumns}
                    dataSource={newTableData || tableData}
                    pagination={false}
                    loading={isFetchingComponents}
                    scroll={{ x: true }}
                    components={tableComponents}
                    onRow={(record, index) => ({ index, record })}
                />
            </DragDropContext>
        </Card>
    )
}

export default ComponentsTable
